import React, { useMemo } from "react";
import { Box, Grid, HStack, Stack, Text } from "@chakra-ui/react";
import { DashboardInput } from "components/utils/FormControls";
import { MdAccessAlarm, MdAccessTime, MdEvent } from "react-icons/md";
import { FaClock } from "react-icons/fa";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { formatFechaRange, formatHoraHHMM, toNullIfEmpty } from "utils/formatters";
import { useDialogForm } from "utils/useDialogForm";

function parseTimeToMinutes(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return null;

  const ampmMatch = /^(\d{1,2})(?::?(\d{2}))?\s*([ap])\.?m?\.?$/.exec(raw);
  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minute = ampmMatch[2] == null ? 0 : Number(ampmMatch[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
    if (ampmMatch[3] === "p" && hour !== 12) hour += 12;
    if (ampmMatch[3] === "a" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }

  const colonMatch = /^(\d{1,2}):(\d{1,2})$/.exec(raw);
  if (colonMatch) {
    const hour = Number(colonMatch[1]);
    const minute = Number(colonMatch[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return hour * 60 + minute;
  }

  const digits = raw.replace(/\D/g, "");
  if (!digits || digits.length > 4) return null;

  const hour = digits.length <= 2 ? Number(digits) : Number(digits.slice(0, -2));
  const minute = digits.length <= 2 ? 0 : Number(digits.slice(-2));
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

function minutesToHHMM(minutes) {
  if (minutes == null) return "";
  const day = 24 * 60;
  const normalized = ((minutes % day) + day) % day;
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function normalizeTimeInput(value) {
  return minutesToHHMM(parseTimeToMinutes(value));
}

export const HorarioCard = ({ actividad, actividadId, patch }) => {
  const dialog = useDialogForm({ fechaInicio: "", fechaFin: "", horaInicio: "", horaFin: "", multiDay: false });
  const [localError, setLocalError] = React.useState(null);
  const [advancedDateOpen, setAdvancedDateOpen] = React.useState(false);

  const saving = patch.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;
  const errorText = localError || patchError;

  const fechaTxt = useMemo(() => formatFechaRange(actividad), [actividad]);
  const horaInicioTxt = useMemo(() => formatHoraHHMM(actividad?.horaInicio), [actividad?.horaInicio]);
  const horaFinTxt = useMemo(() => formatHoraHHMM(actividad?.horaFin), [actividad?.horaFin]);

  function openDialog() {
    const isMultiDay = !!actividad?.fechaFin;
    setLocalError(null);
    setAdvancedDateOpen(isMultiDay);
    dialog.openWith({
      fechaInicio: actividad?.fechaInicio ?? "",
      fechaFin: actividad?.fechaFin ?? "",
      horaInicio: actividad?.horaInicio ?? "",
      horaFin: actividad?.horaFin ?? "",
      multiDay: isMultiDay,
    });
  }

  function setMultiDayEnabled(enabled) {
    setAdvancedDateOpen(enabled);
    dialog.setField("multiDay", enabled);
    if (!enabled) dialog.setField("fechaFin", "");
  }

  function patchHoraInicio(value) {
    dialog.setField("horaInicio", value);
  }

  function normalizeHoraInicio() {
    const normalized = normalizeTimeInput(dialog.form.horaInicio);
    if (!normalized) return;
    dialog.setField("horaInicio", normalized);
    if (!String(dialog.form.horaFin || "").trim()) {
      dialog.setField("horaFin", minutesToHHMM(parseTimeToMinutes(normalized) + 180));
    }
  }

  function normalizeHoraFin() {
    const normalized = normalizeTimeInput(dialog.form.horaFin);
    if (normalized) dialog.setField("horaFin", normalized);
  }

  async function save() {
    setLocalError(null);
    const horaInicio = normalizeTimeInput(dialog.form.horaInicio);
    const horaFin = normalizeTimeInput(dialog.form.horaFin);
    if (String(dialog.form.horaInicio || "").trim() && !horaInicio) {
      setLocalError("La hora de inicio no tiene un formato valido.");
      return;
    }
    if (String(dialog.form.horaFin || "").trim() && !horaFin) {
      setLocalError("La hora de fin no tiene un formato valido.");
      return;
    }

    await patch.mutateAsync({
      actividadId,
      patch: {
        fechaInicio: toNullIfEmpty(dialog.form.fechaInicio),
        fechaFin: toNullIfEmpty(dialog.form.fechaFin),
        horaInicio: toNullIfEmpty(horaInicio),
        horaFin: toNullIfEmpty(horaFin),
      },
    });
    dialog.close();
  }

  return (
    <>
      <DataCard title="Horario" variant="blue" headerIcon={<FaClock size="24px" />} onEdit={openDialog} editAriaLabel="Editar horario">
        <Stack gap="4" alignItems="start">
          <HStack gap="2.5" align="start">
            <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdEvent size="16px" /></Box>
            <Stack gap="0">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Fecha</Text>
              <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{fechaTxt || "Por definir"}</Text>
            </Stack>
          </HStack>

          <Grid templateColumns="repeat(2, 1fr)" gap="3" alignItems="start">
            <HStack gap="2.5" align="start">
              <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdAccessTime size="16px" /></Box>
              <Stack gap="0">
                <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Hora Inicio</Text>
                <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{horaInicioTxt || "-"}</Text>
              </Stack>
            </HStack>

            <HStack gap="2.5" align="start">
              <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdAccessAlarm size="16px" /></Box>
              <Stack gap="0">
                <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Hora Fin</Text>
                <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{horaFinTxt || "-"}</Text>
              </Stack>
            </HStack>
          </Grid>
        </Stack>
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Horario" errorText={errorText} saving={saving} onCancel={dialog.close} onSave={save}>
        <Grid templateColumns={{ base: "1fr", md: advancedDateOpen ? "repeat(2, 1fr)" : "1fr" }} gap="4">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">{advancedDateOpen ? "Fecha Inicio" : "Fecha"}</Text>
            <DashboardInput type="date" value={dialog.form.fechaInicio || ""} onChange={(e) => dialog.setField("fechaInicio", e.target.value)} />
            <Box
              as="button"
              type="button"
              mt="2"
              alignSelf="start"
              color="dashboard.fg.muted"
              fontSize="xs"
              fontWeight="700"
              textDecoration="underline"
              textUnderlineOffset="3px"
              onClick={() => setMultiDayEnabled(!advancedDateOpen)}
              _hover={{ color: "dashboard.accent.fg" }}
              _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}
            >
              {advancedDateOpen ? "Ocultar configuracion avanzada" : "Configuracion avanzada: actividad de varios dias"}
            </Box>
          </Box>

          {advancedDateOpen && (
            <Box>
              <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Fecha Fin</Text>
              <DashboardInput type="date" value={dialog.form.fechaFin || ""} onChange={(e) => dialog.setField("fechaFin", e.target.value)} />
            </Box>
          )}
        </Grid>

        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="4">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Hora Inicio</Text>
            <DashboardInput
              value={dialog.form.horaInicio}
              inputMode="numeric"
              placeholder="Ej. 9:00, 930, 5pm"
              onChange={(e) => patchHoraInicio(e.target.value)}
              onBlur={normalizeHoraInicio}
            />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Hora Fin</Text>
            <DashboardInput
              value={dialog.form.horaFin}
              inputMode="numeric"
              placeholder="Ej. 13:00, 1300, 1pm"
              onChange={(e) => dialog.setField("horaFin", e.target.value)}
              onBlur={normalizeHoraFin}
            />
          </Box>
        </Grid>
      </FormDialog>
    </>
  );
};
